import { useTranslation } from 'react-i18next'
import { Box, styled } from '@mui/material'
import { useIsSmallScreenFalse } from 'packages/core'
import { ListingGrid, GridContainer, Filters } from 'components'
import useSubcomponents from 'useSubcomponents'
import EidTableGrid from 'components/EidTableGrid/EidTableGrid'
import {
    useAppManagementRoleTotalAccessGranted,
    useResourceTypeRoles,
} from 'hooks'
import { Select } from 'packages/eid-ui'
import { useEffect, useState } from 'react'

export const SwitchContainer = styled(({ value, ...rest }) => (
    <Box {...rest} />
))(({ value }) => ({
    display: 'flex',
    alignItems: 'center',
    marginLeft: '4px',
    '& >p': {
        fontSize: '14px',
    },
    '& .Label': {
        color: value ? '#8b909a' : '#000000',
    },
    '& .SubLabel': {
        color: value ? '#000000' : '#8b909a',
    },
    '& .MuiFormControlLabel-root': {
        marginLeft: '10px',
    },
}))

const FilterAndSearchBox = styled(Box)({
    display: 'flex',
    gap: '2rem',
    alignItems: 'end',
    marginTop: '-1.6rem',
})

const TotalAccessGranted = ({ managementRoleId }) => {
    const { t } = useTranslation()
    const { getAccessBasedAttributes } = useSubcomponents()

    const { data: resourceTypeRoles, isLoading: isLoadingResourceTypeRoles } =
        useResourceTypeRoles(managementRoleId)

    const [resourceTypeRole, setResourceTypeRole] = useState(null)

    const {
        list,
        loading: isLoading,
        loadingMore: isLoadingMore,
        pagination,
    } = useAppManagementRoleTotalAccessGranted(
        managementRoleId,
        resourceTypeRole?.id,
        Boolean(resourceTypeRole?.id),
    )

    const isSmallScreen = useIsSmallScreenFalse()

    const attributes = [
        {
            name: 'friendlyName',
            sortable: true,
            sortBy: 'friendlyName',
            label: 'Name',
            isHeader: true,
            showInCard: false,
            //NOTE : Keeping the commented out code for subcomponent traceability
            // requireAccess: {
            //     control:
            //         'ResourceAdmin-ManagementRoleDetails-TotalAccessGranted-FriendlyName',
            // },
        },
        {
            name: 'resourceTypeName',
            sortable: true,
            sortBy: 'resourceTypeName',
            label: 'ResourceType',
            requireAccess: {
                control:
                    'ResourceAdmin-ManagementRoleDetails-TotalAccessGranted-ResourceType',
            },
        },
        {
            name: 'resourceTypeRoleName',
            sortable: true,
            sortBy: 'resourceTypeRoleName',
            label: 'ResourceTypeRole',
            requireAccess: {
                control:
                    'ResourceAdmin-ManagementRoleDetails-TotalAccessGranted-ResourceTypeRole',
            },
        },
        {
            name: 'resourceSystemFriendlyName',
            sortable: true,
            sortBy: 'resourceSystemFriendlyName',
            label: 'ResourceSystem',
            requireAccess: {
                control:
                    'ResourceAdmin-ManagementRoleDetails-TotalAccessGranted-ResourceSystem',
            },
        },
    ]

    useEffect(() => {
        if (resourceTypeRoles?.length > 0) {
            setResourceTypeRole(resourceTypeRoles[0])
        }
    }, [resourceTypeRoles])

    return (
        <Box>
            <ListingGrid>
                <Box
                    display="flex"
                    flexDirection={isSmallScreen ? 'column' : 'row'}
                    alignItems="center"
                    justifyContent="end"
                >
                    <FilterAndSearchBox>
                        <Box minWidth="20rem">
                            <Select
                                label={t('AccessLevel')}
                                options={resourceTypeRoles || []}
                                onChange={(data) => {
                                    setResourceTypeRole(data)
                                }}
                                value={resourceTypeRole}
                                getOptionLabel={(option) => t(option.name)}
                                placeholder={t('AccessLevel')}
                                isLoading={isLoadingResourceTypeRoles}
                            />
                        </Box>
                        <Box width="24rem">
                            <Filters.TextSearch />
                        </Box>
                    </FilterAndSearchBox>
                </Box>
                <GridContainer
                    style={{
                        padding: '16px 0px',
                        marginTop: '0px',
                        backgroundColor: isSmallScreen
                            ? 'white'
                            : 'transparent',
                    }}
                >
                    <EidTableGrid
                        noDataMessage={t('NoDataMessage')}
                        pagination={pagination}
                        isLoading={isLoading}
                        isLoadingMore={isLoadingMore}
                        attributes={getAccessBasedAttributes(attributes)}
                        list={list}
                    />
                </GridContainer>
            </ListingGrid>
        </Box>
    )
}

export default TotalAccessGranted
